import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import ChangePassword from "./ChangePassword";
import BacklogSection from "./modules/BacklogSection";
import ReportSection from "./modules/ReportSection";
import ProjectRankingSection from "./modules/ProjectRankingSection";
import CompletedTasksSection from "./modules/CompletedTasksSection";
import AllTasksSection from "./modules/AllTasksSection";
import PendingTasksSection from "./modules/PendingTasksSection";
import NewProjectSection from "./modules/NewProjectSection";

const AdminDashboard = () => {
    const { username, logout } = useAuth();
    const navigate = useNavigate();
    const BASE_URL = process.env.REACT_APP_BACKEND_URL;

    // ---------------------- STATES ----------------------
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showChangePassword, setShowChangePassword] = useState(false);

    // Drawer
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Sections
    const [showReportSection, setShowReportSection] = useState(false);
    const [developers, setDevelopers] = useState([]);
    const [selectedDev, setSelectedDev] = useState("");
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState("");
    const [report, setReport] = useState(null);
    const [showBacklogForm, setShowBacklogForm] = useState(false);
    const [backlogDeveloper, setBacklogDeveloper] = useState("");
    const [backlogProject, setBacklogProject] = useState("");
    const [backlogDescription, setBacklogDescription] = useState("");
    const [backlogTasks, setBacklogTasks] = useState([]);
    const [showProjectRanking, setShowProjectRanking] = useState(false);
    const [showNewProject, setShowNewProject] = useState(false);

    // Pagination
    const [currentBacklogPage, setCurrentBacklogPage] = useState(1);
    const backlogsPerPage = 5;

    // Popup
    const [popupMessage, setPopupMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    // ✅ Dropdown Task View Mode
    const [viewMode, setViewMode] = useState("all"); // all | pending | completed

    const downloadExcel = async () => {
        try {
            const response = await fetch(`${BASE_URL}/admin/export-db`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                }
            });

            console.log("Response status:", response.status);
            console.log("Response headers:", [...response.headers.entries()]);

            if (!response.ok) {
                throw new Error("Failed to download file");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = "database_export.xlsx";
            link.click();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Excel download failed:", error);
            alert("❌ Failed to download Excel file. Check console.");
        }
    };

    // ---------------------- FETCH ALL TASKS ----------------------
    useEffect(() => {
        const fetchAllTasks = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${BASE_URL}/admin/tasks`);
                if (!res.ok) throw new Error("Failed to fetch tasks");
                const data = await res.json();
                setTasks(data);
                setFilteredTasks(data);
            } catch (err) {
                console.error("Error fetching tasks:", err);
                setError("Failed to fetch tasks from server");
            } finally {
                setLoading(false);
            }
        };
        fetchAllTasks();
    }, [BASE_URL]);


    // ---------------------- LOGOUT ----------------------
    const handleLogout = () => {
        logout();
        window.location.href = "/";
    };

    // ---------------------- REPORT SECTION ----------------------
    const toggleReportSection = async () => {
        const newState = !showReportSection;
        setShowReportSection(newState);
        setShowBacklogForm(false);
        setShowProjectRanking(false);
        setReport(null);
        if (newState) await fetchDevelopers();
    };

    const fetchDevelopers = async () => {
        try {
            const res = await fetch(`${BASE_URL}/admin/developers`);
            if (!res.ok) throw new Error("Failed to fetch developers");
            const data = await res.json();
            setDevelopers(data);
        } catch (err) {
            console.error("Error fetching developers:", err);
            setError("Failed to load developers");
        }
    };

    const fetchProjects = async (developerId) => {
        try {
            const res = await fetch(`${BASE_URL}/admin/developer-projects/${developerId}`);
            if (!res.ok) throw new Error("Failed to fetch projects");
            const data = await res.json();
            setProjects(data);
        } catch (err) {
            console.error("Error fetching projects:", err);
            setError("Failed to load projects");
        }
    };

    const handleDeveloperSelect = (e) => {
        const devId = e.target.value;
        setSelectedDev(devId);
        setSelectedProject("");
        setReport(null);
        if (devId) fetchProjects(devId);
    };

    const handleGenerateReport = async () => {
        if (!selectedDev || !selectedProject) {
            alert("Please select both Developer and Project.");
            return;
        }
        try {
            const res = await fetch(`${BASE_URL}/admin/report/${selectedDev}/${selectedProject}`);
            if (!res.ok) throw new Error("Failed to generate report");
            const data = await res.json();
            setReport(data);
        } catch (err) {
            console.error("Error generating report:", err);
            setError("Failed to generate report");
        }
    };

    // ---------------------- BACKLOG SECTION ----------------------
    const fetchBacklogTasks = async () => {
        try {
            const res = await fetch(`${BASE_URL}/admin/backlogs`);
            if (!res.ok) throw new Error("Failed to fetch backlog tasks");
            const data = await res.json();
            setBacklogTasks(data);
        } catch (err) {
            console.error("Error fetching backlog tasks:", err);
            setError("Failed to load backlog tasks");
        }
    };

    const toggleBacklogForm = async () => {
        const newState = !showBacklogForm;
        setShowBacklogForm(newState);
        setShowReportSection(false);
        setShowProjectRanking(false);
        if (newState) {
            await fetchDevelopers();
            await fetchBacklogTasks();
        }
    };

    const showCenteredPopup = (message) => {
        setPopupMessage(message);
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 2500);
    };

    const handleBacklogSubmit = async (e) => {
        e.preventDefault();
        if (!backlogDeveloper || !backlogProject || !backlogDescription) {
            alert("Please fill all fields before submitting.");
            return;
        }
        try {
            const res = await fetch(`${BASE_URL}/admin/backlog`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    developer_id: backlogDeveloper,
                    project_name: backlogProject,
                    description: backlogDescription,
                }),
            });
            if (!res.ok) throw new Error("Failed to add backlog task");
            showCenteredPopup("✅ Backlog task successfully added!");
            setShowBacklogForm(false);
            setBacklogDeveloper("");
            setBacklogProject("");
            setBacklogDescription("");
            const data = await (await fetch(`${BASE_URL}/admin/tasks`)).json();
            setTasks(data);
            setFilteredTasks(data);
        } catch (err) {
            console.error("Error adding backlog:", err);
            showCenteredPopup("❌ Failed to add backlog task.");
        }
    };

    // ---------------------- PROJECT RANKING SECTION ----------------------
    const toggleProjectRanking = () => {
        const newState = !showProjectRanking;
        setShowProjectRanking(newState);
        setShowBacklogForm(false);
        setShowReportSection(false);
    };

    // ---------------------- RENDER ----------------------
    return (
        <div className="min-h-screen bg-gray-900 text-white p-8">
            {/* HEADER */}
            <header className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold">
                    Admin Dashboard - Welcome, <span className="text-indigo-400">{username}</span> 👋
                </h1>

                <div className="flex items-center space-x-3">
                    {/* 🏠 Home Button */}
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-md text-lg"
                        title="Refresh Dashboard"
                    >
                        🏠
                    </button>

                    {/* ☰ Drawer (Menu) Button */}
                    <button
                        onClick={() => setDrawerOpen(true)}
                        className="bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-md text-lg"
                        title="Open Menu"
                    >
                        ☰
                    </button>
                </div>
            </header>

            {/* DRAWER */}
            {drawerOpen && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setDrawerOpen(false)}></div>
                    <div className="fixed top-0 right-0 h-full w-72 bg-gray-900 text-white shadow-2xl transform transition-transform duration-300 z-50 translate-x-0">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700">
                            <h2 className="text-xl font-semibold">Menu</h2>
                            <button onClick={() => setDrawerOpen(false)} className="text-gray-400 hover:text-white text-2xl">
                                ×
                            </button>
                        </div>

                        <nav className="flex flex-col p-6 space-y-3">
                            <button
                                onClick={() => {
                                    navigate("/signup");
                                    setDrawerOpen(false);
                                }}
                                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md text-left font-medium"
                            >
                                🧑‍💻 Create New User
                            </button>

                            <button
                                onClick={() => {
                                    setShowNewProject(true);
                                    setDrawerOpen(false);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-left font-medium"
                            >
                                🧩 Add Project
                            </button>


                            <button
                                onClick={() => {
                                    toggleReportSection();
                                    setDrawerOpen(false);
                                }}
                                className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-md text-left font-medium"
                            >
                                📊 Calculate Efforts
                            </button>
                            <button
                                onClick={() => {
                                    toggleBacklogForm();
                                    setDrawerOpen(false);
                                }}
                                className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded-md text-left font-medium"
                            >
                                🧱 Backlog Task
                            </button>
                            <button
                                onClick={() => {
                                    toggleProjectRanking();
                                    setDrawerOpen(false);
                                }}
                                className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-md text-left font-medium"
                            >
                                🏆 View Project Ranking
                            </button>

                            <button
                                onClick={() => {
                                    downloadExcel();
                                    setDrawerOpen(false);
                                }}
                                className="bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-md text-left font-medium"
                            >
                                📥 Download DB (Excel)
                            </button>

                            <div className="border-t border-gray-700 my-2"></div>
                            <button
                                onClick={() => {
                                    setShowChangePassword(true);
                                    setDrawerOpen(false);
                                }}
                                className="bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-md text-left font-medium"
                            >
                                🔐 Change Password
                            </button>
                            <button
                                onClick={() => {
                                    handleLogout();
                                    setDrawerOpen(false);
                                }}
                                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-md text-left font-medium"
                            >
                                🚪 Logout
                            </button>
                        </nav>
                    </div>
                </>
            )}

            {/* Conditional Sections */}
            {showBacklogForm && (
                <BacklogSection
                    developers={developers}
                    backlogTasks={backlogTasks}
                    currentBacklogs={backlogTasks.slice(
                        (currentBacklogPage - 1) * backlogsPerPage,
                        currentBacklogPage * backlogsPerPage
                    )}
                    backlogDeveloper={backlogDeveloper}
                    backlogProject={backlogProject}
                    backlogDescription={backlogDescription}
                    onDeveloperChange={(e) => setBacklogDeveloper(e.target.value)}
                    onProjectChange={(e) => setBacklogProject(e.target.value)}
                    onDescriptionChange={(e) => setBacklogDescription(e.target.value)}
                    onSubmit={handleBacklogSubmit}
                    currentPage={currentBacklogPage}
                    totalPages={Math.ceil(backlogTasks.length / backlogsPerPage)}
                    setCurrentPage={setCurrentBacklogPage}
                />
            )}

            {showReportSection && (
                <ReportSection
                    developers={developers}
                    projects={projects}
                    selectedDev={selectedDev}
                    selectedProject={selectedProject}
                    report={report}
                    onDeveloperChange={handleDeveloperSelect}
                    onProjectChange={(e) => setSelectedProject(e.target.value)}
                    onGenerate={handleGenerateReport}
                />
            )}

            {showProjectRanking && <ProjectRankingSection BASE_URL={BASE_URL} />}

            {error && <p className="text-red-500 mb-4 text-center font-medium">{error}</p>}

            {/* ---------------- TASK VIEW MODES ---------------- */}
            <div className="flex flex-col md:flex-row md:justify-end md:items-center mb-6 space-y-4 md:space-y-0">
                {/* 📋 Task View Dropdown */}
                <select
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    className="bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                    <option value="all">All</option>
                    <option value="pending">Pending Tasks</option>
                    <option value="completed">Completed Tasks</option>
                </select>
            </div>


            {loading ? (
                <p className="text-center text-gray-400 my-8 text-lg animate-pulse">
                    Loading tasks...
                </p>
            ) : (
                <>
                    {viewMode === "all" && (
                        <AllTasksSection BASE_URL={BASE_URL} tasks={filteredTasks} />
                    )}
                    {viewMode === "pending" && (
                        <PendingTasksSection BASE_URL={BASE_URL} />
                    )}
                    {viewMode === "completed" && (
                        <CompletedTasksSection BASE_URL={BASE_URL} />
                    )}
                </>
            )}

            {/* Change Password */}
            {showChangePassword && (
                <ChangePassword
                    username={username}
                    role="Admin"
                    onClose={() => setShowChangePassword(false)}
                    onPasswordChangeSuccess={() => setShowChangePassword(false)}
                />
            )}

            {/* New Project Modal */}
            {showNewProject && (
                <NewProjectSection
                    BASE_URL={BASE_URL}
                    onClose={() => setShowNewProject(false)}
                    showCenteredPopup={showCenteredPopup}
                />
            )}

            {/* Popup */}
            {showPopup && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-gray-800 text-white rounded-2xl shadow-2xl px-8 py-6 text-center animate-fadeIn">
                        <p className="text-lg font-medium">{popupMessage}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
